import {StateCreator} from "zustand/vanilla";
import {ArtifactDescription, ArtifactWithMeta} from "@/app/data/types";
import {ArtifactIdea, ArtifactsList} from "@/pb/api/devplan/types/artifacts";
import {createArtifact, deleteArtifact, updateArtifact} from "@/app/data/actions/artifacts";
import {queryClient} from "@/hooks/queries/queryClient";
import {artifactKeys} from "@/hooks/queries/queryKeys";
import {
  GetArtifactsListResponse,
  GetRepoResearchRequest,
  GetRepoResearchResponse,
  GetRepoResearchGuidanceRequest,
  GetRepoResearchGuidanceResponse,
  GetRepoSummariesResponse,
  GetSitesSummariesResponse,
  UpsertArtifactsListRequest,
  UpsertArtifactsListResponse
} from "@/pb/api/devplan/services/web/company";
import {
  artifactsListAPI,
  artifactsSingleListAPI,
  repoGuidanceAPI,
  repoResearchAPI,
  repoSummariesAPI,
  websiteAnalysisAPI
} from "@/app/apiPaths";
import {jr} from "@/hooks/queries/response-handler";
import {RepositorySummary, WebSiteSummary} from "@/pb/api/devplan/types/integrations";
import {CodeResearchAreaMeta} from "@devplan/contextify-api";
import {getRepoResearchKey, getRepoGuidanceKey} from "@/hooks/queries/stores/util";
import {loadRepositorySummaries} from "./artifactsStore_loadRepoSummaries";
import {ResearchArea} from "@/app/data/code/research";

export type StoredArtifact = ArtifactIdea | ArtifactWithMeta;

export type ArtifactsStoreState = {
  artifacts: Record<string, StoredArtifact>;
  setArtifact(art: StoredArtifact): void;
  setArtifacts<T extends StoredArtifact>(arts: T[]): void;
  getTypedArtifact<T extends StoredArtifact>(id: string): T | null;
  getTypedArtifacts<T extends StoredArtifact>(companyId: number, def: ArtifactDescription<T>): T[];
  createArtifact<T extends StoredArtifact>(companyId: number, art: T, def: ArtifactDescription<T>): Promise<void>;
  updateArtifact<T extends StoredArtifact>(
    companyId: number, id: string, def: ArtifactDescription<T>, updater: (art: T) => T | undefined,
  ): Promise<void>;
  deleteArtifact<T extends StoredArtifact>(companyId: number, id: string, def: ArtifactDescription<T>): Promise<void>;

  artifactsLists: Record<string, ArtifactsList>
  fetchArtifactsList(companyId: number, id: string): Promise<string | null>
  upsertArtifactsList(companyId: number, id: string, updater: (l: ArtifactsList) => ArtifactsList): Promise<string>

  // RepositorySummary mapped by repoSummary.id
  repoSummaries: Record<string, RepositorySummary>;
  // RepoResearchResponse.research string mapped by repoResearchKey
  repoResearches: Record<string, string>;
  // Metas mapped by full repo name
  repoResearchMetas: Record<string, CodeResearchAreaMeta[]>;
  // Research areas mapped by full repo name
  repoResearchAreas: Record<string, ResearchArea[]>;
  // Repo guidance content mapped by guidance key
  repoGuidances: Record<string, string>;
  fetchRepoSummaries(companyId: number): Promise<void>;
  fetchRepoResearch(companyId: number, req: GetRepoResearchRequest): Promise<void>;
  fetchRepoGuidance(companyId: number, req: GetRepoResearchGuidanceRequest): Promise<void>;

  siteSummaries: Record<string, WebSiteSummary>;
  fetchSiteSummaries(companyId: number): Promise<void>;
}

export const createArtifactsSlice: StateCreator<
  ArtifactsStoreState,
  [],
  [],
  ArtifactsStoreState
> = ((set, get) => ({
  artifacts: {},
  repoSummaries: {},
  artifactsLists: {},
  siteSummaries: {},
  repoResearches: {},
  repoResearchMetas: {},
  repoResearchAreas: {},
  repoGuidances: {},
  setArtifact: async art => set(s => ({...s, artifacts: {...s.artifacts, [art.id]: art}})),
  setArtifacts: (arts) => set(s => {
    const artifacts = {...s.artifacts}
    arts.forEach(a => artifacts[a.id] = a)
    return {...s, artifacts}
  }),
  getTypedArtifact<T extends StoredArtifact>(id: string) {
    const art = get().artifacts[id]
    return art ? art as T : null
  },
  getTypedArtifacts<T extends StoredArtifact>(companyId: number, def: ArtifactDescription<T>) {
    const s = get()
    const result = Object.values(s.artifacts)
      .filter(a => a.artifactMeta?.companyId === companyId && a.artifactMeta?.type === def.type)
    result.sort((a, b) => a.id.localeCompare(b.id))
    return result as T[]
  },
  async createArtifact<T extends StoredArtifact>(companyId: number, art: T, def: ArtifactDescription<T>) {
    const created = await createArtifact(companyId, art, def.type)
    get().setArtifact(created)
    await queryClient.invalidateQueries({queryKey: artifactKeys.all(companyId, def.type)})
  },
  async updateArtifact<T extends StoredArtifact>(
    companyId: number, id: string, def: ArtifactDescription<T>, updater: (art: T) => T | undefined,
  ) {
    const art = get().getTypedArtifact<T>(id)
    if (!art) {
      return
    }
    const updated = updater(art)
    if (!updated) {
      return
    }
    const fromDB = await updateArtifact(companyId, id, updated, def.type)
    get().setArtifact(fromDB)
  },
  async deleteArtifact<T extends StoredArtifact>(companyId: number, id: string, def: ArtifactDescription<T>) {
    await deleteArtifact(companyId, id, def.type)
    set(s => {
      const {[id]: _, ...artifacts} = s.artifacts
      return {...s, artifacts}
    })
    await queryClient.invalidateQueries({queryKey: artifactKeys.all(companyId, def.type)})
  },
  fetchRepoSummaries: async (companyId) => fetch(repoSummariesAPI(companyId))
    .then(jr)
    .then(js => {
      const resp = GetRepoSummariesResponse.fromJSON(js)
      set(s => {
        const { 
          summaries,
          repoResearchMetas,
          repoResearchAreas
        }: { 
          summaries: Record<string, RepositorySummary>;
          repoResearchMetas: Record<string, CodeResearchAreaMeta[]>;
          repoResearchAreas: Record<string, ResearchArea[]>;
        } = loadRepositorySummaries(resp);        
        return {
          ...s,
          repoSummaries: {...s.repoSummaries, ...summaries},
          repoResearchMetas: {...s.repoResearchMetas, ...repoResearchMetas},
          repoResearchAreas: {...s.repoResearchAreas, ...repoResearchAreas},
        }
      })
    }),
  fetchRepoResearch: async (companyId, req) => fetch(repoResearchAPI(companyId), {
    method: "POST", body: JSON.stringify(GetRepoResearchRequest.toJSON(req))
  }).then(jr).then(js => {
    const {research} = GetRepoResearchResponse.fromJSON(js)
    set(s => ({...s, repoResearches: {...s.repoResearches, [getRepoResearchKey(req)]: research ?? ""}}))
  }),
  fetchRepoGuidance: async (companyId, req) => fetch(repoGuidanceAPI(companyId), {
    method: "POST", body: JSON.stringify(GetRepoResearchGuidanceRequest.toJSON(req))
  }).then(jr).then(js => {
    const {content} = GetRepoResearchGuidanceResponse.fromJSON(js)
    set(s => ({...s, repoGuidances: {...s.repoGuidances, [getRepoGuidanceKey(req)]: content ?? ""}}))
  }),
  fetchSiteSummaries: async (companyId) => fetch(websiteAnalysisAPI(companyId))
    .then(jr)
    .then(js => {
      const {siteSummaries} = GetSitesSummariesResponse.fromJSON(js)
      set(s => {
        const summaries = siteSummaries.reduce((a, s) => ({...a, [s.url]: s}), {} as Record<string, WebSiteSummary>)
        return {...s, siteSummaries: {...s.siteSummaries, ...summaries}}
      })
    }),
  fetchArtifactsList: async (companyId, id) => fetch(artifactsSingleListAPI(companyId, id))
    .then(r => r.ok ? r.json() : Promise.reject(new Error(r.statusText)))
    .then(js => {
      const {list} = GetArtifactsListResponse.fromJSON(js)
      set(s => {
        if (list) {
          return {...s, artifactsLists: {...s.artifactsLists, [id]: list}}
        }
        const {[id]: _, ...artifactsLists} = s.artifactsLists;
        return {...s, artifactsLists};
      })
      return list?.id || null
    }),
  upsertArtifactsList: async (companyId, id, updater) => {
    const updated = updater(get().artifactsLists[id] || {companyId, id})
    set(s => ({...s, artifactsLists: {...s.artifactsLists, [updated.id]: updated}}))
    return fetch(artifactsListAPI(updated.companyId), {
      method: 'POST', body: JSON.stringify(UpsertArtifactsListRequest.toJSON({list: updated}))
    }).then(r => r.ok ? r.json() : Promise.reject(new Error(r.statusText)))
      .then(js => {
        const {list} = UpsertArtifactsListResponse.fromJSON(js)
        set(s => ({...s, artifactsLists: {...s.artifactsLists, [list!.id]: list!}}))
        return list!.id;
      })
  }
}))